import clsx from 'clsx';
import type React from 'react';

import { VerticalAlignment } from '@gitbook/api-client';
import type { TableDefinitions, TableGridView } from '@gitbook/doc-core';
import type { EditorContainer } from '@gitbook/slate-react';

import { DefinitionTitleView } from '../../ColumnDefinitions/DefinitionTitleView';
import { RecordValue } from '../../ColumnDefinitions/RecordValue';
import type { TableRecordKV } from '../../types';
import { VerticalAlignmentContext } from '../../utils/contexts';

interface ColumnDragPreviewProps {
    columnId: string;
    records: TableRecordKV[];
    definition: TableDefinitions;
    view: TableGridView;
    editor: EditorContainer;
    tableKey: string;
    width: number;
    rowHeights: Record<string, number>;
    isTooFar: boolean;
}

export function ColumnDragPreview({
    columnId,
    records,
    definition,
    view,
    editor,
    tableKey,
    width,
    rowHeights,
    isTooFar,
}: ColumnDragPreviewProps) {
    const columnDef = definition[columnId];
    const style: React.CSSProperties = {
        width,
        maxWidth: width,
        boxShadow: isTooFar
            ? '0px 0px 0px 2px var(--sp-ref-color-scarlet-50)'
            : 'var(--sp-shadow-elevated)',
    };

    return (
        <div
            className={clsx(
                'z-50 flex cursor-grabbing flex-col overflow-hidden border bg-base transition-all',
                isTooFar
                    ? 'border-[var(--sp-ref-color-scarlet-300)]'
                    : 'border-[var(--sp-color-border-table-active-outer)]'
            )}
            style={style}
        >
            {/* Header Cell */}
            <div className="pointer-events-none flex shrink-0 items-center border-control border-b bg-muted px-3 py-3">
                <div className="truncate whitespace-nowrap text-left font-medium ">
                    <DefinitionTitleView
                        editor={editor}
                        nodeKey={tableKey}
                        columnDef={columnDef}
                        readOnly={false}
                    />
                </div>
            </div>

            {/* Body Cells */}
            {records.map((record, index) => {
                const rowHeight = rowHeights[record.key] ?? 'auto';
                const verticalAlignment =
                    columnDef.type === 'text' && columnDef.verticalAlignment
                        ? columnDef.verticalAlignment
                        : VerticalAlignment.Middle;

                return (
                    <VerticalAlignmentContext.Provider value={verticalAlignment} key={record.key}>
                        <div
                            className={clsx(
                                'pointer-events-none flex overflow-hidden whitespace-nowrap border-control px-3 py-3 ',
                                verticalAlignment === 'middle' && 'items-center',
                                verticalAlignment === 'top' && 'items-start',
                                verticalAlignment === 'bottom' && 'items-end',
                                index !== records.length - 1 && 'border-b '
                            )}
                            style={{ height: rowHeight }}
                        >
                            <RecordValue
                                definitionColorKind={undefined}
                                record={record}
                                selected={false}
                                columnDefinition={columnDef}
                                nodeKey={tableKey}
                                editor={editor}
                                readOnly={false}
                                view={view}
                            />
                        </div>
                    </VerticalAlignmentContext.Provider>
                );
            })}
        </div>
    );
}
